import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/** Where the main navigation lives: down the left, or across the top. */
export type NavMode = 'sidebar' | 'navbar';

interface UiState {
  navMode: NavMode;
  /** Sidebar reduced to icons only. Only meaningful in `sidebar` mode. */
  sidebarCollapsed: boolean;
  /** The off-canvas menu on small screens. Never persisted. */
  mobileNavOpen: boolean;

  setNavMode: (mode: NavMode) => void;
  toggleNavMode: () => void;
  toggleSidebar: () => void;
  setMobileNavOpen: (open: boolean) => void;
}

/**
 * Client-side UI state only. Anything that comes from the API belongs in a
 * TanStack Query cache, not here.
 *
 * The layout preferences persist to localStorage, so the app opens the way the
 * user left it - on the same machine, without needing a server round trip or a
 * user record to hang the preference off.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      navMode: 'sidebar',
      sidebarCollapsed: false,
      mobileNavOpen: false,

      setNavMode: (mode) => {
        set({ navMode: mode, mobileNavOpen: false });
      },
      toggleNavMode: () => {
        set({
          navMode: get().navMode === 'sidebar' ? 'navbar' : 'sidebar',
          mobileNavOpen: false,
        });
      },
      toggleSidebar: () => {
        set({ sidebarCollapsed: !get().sidebarCollapsed });
      },
      setMobileNavOpen: (open) => {
        set({ mobileNavOpen: open });
      },
    }),
    {
      name: 'windocs.ui',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      /* Only the deliberate preferences are remembered. `mobileNavOpen` is
         transient - restoring an open overlay on load would be baffling. */
      partialize: (state) => ({
        navMode: state.navMode,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    },
  ),
);
